import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  updateDoc,
} from "firebase/firestore";
import { db } from "../../../../firebase";

const COLLECTION_NAME = "classroomAudioReactions";

/**
 * Collect unique non-empty values from an array of objects for a given key.
 */
const collectUniqueIds = (rows, key) => {
  const ids = new Set();
  rows.forEach((r) => {
    if (r[key]) ids.add(r[key]);
  });
  return Array.from(ids);
};

/**
 * Batch-fetch documents by their IDs from a given collection.
 * Returns a Map of id → document data.
 */
const fetchDocsByIds = async (collectionName, ids) => {
  const map = new Map();
  if (!ids || ids.length === 0) return map;

  const promises = ids.map(async (id) => {
    try {
      const snap = await getDoc(doc(db, collectionName, id));
      if (snap.exists()) {
        map.set(id, { id: snap.id, ...snap.data() });
      }
    } catch (e) {
      console.warn(`Failed to fetch ${collectionName}/${id}:`, e.message);
    }
  });

  await Promise.all(promises);
  return map;
};

/**
 * Enrich classroom audio reaction rows with related entity names.
 * Adds: className, activityName, quicktaleTitle, submittedBy
 */
const enrichClassroomAudioReactions = async (rows) => {
  if (!rows || rows.length === 0) return rows;

  // Collect unique IDs for each related collection
  const groupIds = collectUniqueIds(rows, "groupId");
  const activityIds = collectUniqueIds(rows, "activityId");
  const quicktaleIds = collectUniqueIds(rows, "quicktaleId");
  const userIds = collectUniqueIds(rows, "createdBy");

  // Fetch all related documents in parallel
  const [groupsMap, activitiesMap, quicktalesMap, usersMap] = await Promise.all([
    fetchDocsByIds("group", groupIds),
    fetchDocsByIds("activities", activityIds),
    fetchDocsByIds("quicktales", quicktaleIds),
    fetchDocsByIds("users", userIds),
  ]);

  // Merge resolved names onto each row
  return rows.map((row) => {
    const group = groupsMap.get(row.groupId);
    const activity = activitiesMap.get(row.activityId);
    const quicktale = quicktalesMap.get(row.quicktaleId);
    const user = usersMap.get(row.createdBy);

    return {
      ...row,
      className: group?.name || "",
      activityName: activity?.name || "",
      reactionTo: row.activitySubmissionId ? "Classmate" : "Prompt",
      quicktaleTitle: quicktale?.title || "",
      submittedBy: user
        ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
        : "",
    };
  });
};

export const getAllClassroomAudioReactions = async () => {
  const colRef = collection(db, COLLECTION_NAME);
  const q = query(colRef, orderBy("createdAt", "desc"));
  const snapshot = await getDocs(q);
  const rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  return enrichClassroomAudioReactions(rows);
};

export const getSingleClassroomAudioReaction = async (id) => {
  const docRef = doc(db, COLLECTION_NAME, id);
  return getDoc(docRef);
};

export const updateClassroomAudioReaction = async (id, data) => {
  const docRef = doc(db, COLLECTION_NAME, id);
  return updateDoc(docRef, data);
};

/**
 * Client-side search across enriched classroom audio reaction rows.
 * Searches: className, activityName, quicktaleTitle, submittedBy, id
 */
export const classroomAudioReactionsSearch = (array, keyword) => {
  const searchTerm = keyword.toLowerCase();
  return (array || []).filter((row) => {
    return (
      (row.className && row.className.toLowerCase().includes(searchTerm)) ||
      (row.activityName && row.activityName.toLowerCase().includes(searchTerm)) ||
      (row.reactionTo && row.reactionTo.toLowerCase().includes(searchTerm)) ||
      (row.quicktaleTitle && row.quicktaleTitle.toLowerCase().includes(searchTerm)) ||
      (row.submittedBy && row.submittedBy.toLowerCase().includes(searchTerm)) ||
      (row.id && row.id.toLowerCase().includes(searchTerm))
    );
  });
};
